/**
 * Phase 31 — Franchisee-Only Wholesale Bundle Options admin page.
 *
 * Route: /admin/partners/wholesale-bundles
 *
 * PINNED USER RULE (Feb 2026):
 *   Configuration + visibility only. NOT a shop. NO carts, checkouts,
 *   Stripe, invoices, stock movement, fulfilment, supplier orders,
 *   shipping labels, partner/customer notifications, emails, SMS,
 *   commission, white-label, partner-status, preferred-status or
 *   public-allocation changes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Plus, Archive, Save,
  History, Package, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


const BUNDLE_TYPE_LABEL = {
  sample_kit:           "Sample kit bundle",
  bottle_label:         "Bottle / label bundle",
  field_label_qr:       "Field label · QR pack",
  marketing_material:   "Marketing material bundle",
  testing_pack:         "Testing pack bundle",
  retest_promotion:     "Retest promotion pack",
  franchisee_onboarding: "Franchisee onboarding pack",
  consumables:          "Consumables bundle",
  other:                "Other",
};


const STATUS_LABEL = {
  draft:    "Draft",
  active:   "Active",
  paused:   "Paused",
  archived: "Archived",
};


const PRICE_STATUS_LABEL = {
  estimate:  "Estimate",
  confirmed: "Confirmed",
  hidden:    "Hidden from partner",
};


function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  return `$${Number(v).toFixed(2)}`;
}


function fmtAU(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}


function statusPillClass(status) {
  switch (status) {
    case "active":   return "bg-emerald-50 text-emerald-800 border-emerald-200";
    case "draft":    return "bg-slate-50 text-slate-700 border-slate-200";
    case "paused":   return "bg-amber-50 text-amber-800 border-amber-200";
    case "archived": return "bg-rose-50 text-rose-700 border-rose-200";
    default:         return "bg-slate-50 text-slate-700 border-slate-200";
  }
}


function emptyDraft() {
  return {
    bundle_name: "",
    bundle_type: "sample_kit",
    description: "",
    included_items: [],
    eligible_partner_type: "pws_franchisee",
    eligible_partner_status: "active",
    visible_to_partner: false,
    admin_only: true,
    wholesale_price: "",
    rrp_reference: "",
    gst_inclusive: true,
    minimum_order_quantity: "",
    price_status: "estimate",
    fulfilment_note: "",
    internal_note: "",
    status: "draft",
  };
}


function toDraft(bundle) {
  return {
    bundle_name:             bundle.bundle_name || "",
    bundle_type:             bundle.bundle_type || "sample_kit",
    description:             bundle.description || "",
    included_items:          (bundle.included_items || []).map((i) => ({
      label:    i.label || "",
      quantity: i.quantity ?? "",
      note:     i.note || "",
    })),
    eligible_partner_type:   bundle.eligible_partner_type || "pws_franchisee",
    eligible_partner_status: bundle.eligible_partner_status || "active",
    visible_to_partner:      !!bundle.visible_to_partner,
    admin_only:              !!bundle.admin_only,
    wholesale_price:         bundle.wholesale_price ?? "",
    rrp_reference:           bundle.rrp_reference ?? "",
    gst_inclusive:           bundle.gst_inclusive !== false,
    minimum_order_quantity:  bundle.minimum_order_quantity ?? "",
    price_status:            bundle.price_status || "estimate",
    fulfilment_note:         bundle.fulfilment_note || "",
    internal_note:           bundle.internal_note || "",
    status:                  bundle.status || "draft",
  };
}


function draftToPayload(draft) {
  return {
    bundle_name:             draft.bundle_name.trim(),
    bundle_type:             draft.bundle_type,
    description:             draft.description,
    included_items:          draft.included_items
      .filter((i) => (i.label || "").trim())
      .map((i) => ({
        label:    i.label.trim(),
        quantity: i.quantity === "" ? null : Number(i.quantity),
        note:     i.note,
      })),
    eligible_partner_type:   draft.eligible_partner_type,
    eligible_partner_status: draft.eligible_partner_status,
    visible_to_partner:      draft.visible_to_partner,
    admin_only:              draft.admin_only,
    wholesale_price:         draft.wholesale_price === ""
      ? null : Number(draft.wholesale_price),
    rrp_reference:           draft.rrp_reference === ""
      ? null : Number(draft.rrp_reference),
    gst_inclusive:           draft.gst_inclusive,
    minimum_order_quantity:  draft.minimum_order_quantity === ""
      ? null : Number(draft.minimum_order_quantity),
    price_status:            draft.price_status,
    fulfilment_note:         draft.fulfilment_note,
    internal_note:           draft.internal_note,
    status:                  draft.status,
  };
}


export default function PWSWholesaleBundles() {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);     // null=closed, "" = new
  const [draft, setDraft] = useState(null);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pws-wholesale-bundles/list");
      setRows(r.data.rows || []);
      setMeta(r.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail)
                  || "Failed to load bundles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditingId("");
    setDraft(emptyDraft());
    setAudit([]);
  };

  const openExisting = async (bundle) => {
    setEditingId(bundle.id);
    setDraft(toDraft(bundle));
    try {
      const a = await api.get(
        `/admin/pws-wholesale-bundles/${bundle.id}/audit`,
      );
      setAudit(a.data.rows || []);
    } catch {
      setAudit([]);
    }
  };

  const closeEditor = () => {
    setEditingId(null); setDraft(null); setAudit([]);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      if (editingId === "") {
        await api.post("/admin/pws-wholesale-bundles",
                       draftToPayload(draft));
        toast.success("Bundle created", { duration: 6000 });
      } else {
        await api.put(
          `/admin/pws-wholesale-bundles/${editingId}`,
          draftToPayload(draft),
        );
        toast.success("Bundle saved", { duration: 6000 });
      }
      await load();
      closeEditor();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail)
                  || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (bundle) => {
    if (!window.confirm(
        `Archive "${bundle.bundle_name}"? This hides it from any partner view.`)) return;
    try {
      await api.post(
        `/admin/pws-wholesale-bundles/${bundle.id}/archive`,
      );
      toast.success("Bundle archived");
      await load();
      if (editingId === bundle.id) closeEditor();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail)
                  || "Archive failed");
    }
  };

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (a.status === "archived" && b.status !== "archived") return 1;
      if (b.status === "archived" && a.status !== "archived") return -1;
      return (a.bundle_name || "").localeCompare(b.bundle_name || "");
    });
  }, [rows]);

  return (
    <PortalShell>
      <div
        data-testid="ph31-wholesale-bundles-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-7"
      >
        <div>
          <Link
            to="/admin/partners/network"
            className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Partners
          </Link>
          <h1
            data-testid="ph31-page-title"
            className="font-display mt-2 text-3xl sm:text-4xl font-bold text-slate-900"
          >
            Franchisee Wholesale Bundles
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-3xl">
            Internal partner catalogue — admin reference only. This
            page does not place orders, does not take payments and
            does not trigger fulfilment.
          </p>
        </div>

        <div
          data-testid="ph31-safety-banner"
          className="flex items-start gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4 text-sm text-emerald-900"
        >
          <ShieldCheck className="w-5 h-5 mt-0.5 text-emerald-600 flex-shrink-0" />
          <div>
            <div className="font-semibold">Visibility only.</div>
            <div className="mt-0.5">
              {meta?.safety_banner ||
                "Internal partner catalogue · visibility only · this is not an order, not a quote, and no fulfilment is triggered from this page. Availability and pricing to be confirmed by Aware."}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg font-bold text-slate-800">
            Catalogue ({rows.length})
          </h2>
          <button
            data-testid="ph31-new-bundle-btn"
            onClick={openNew}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-cyan-600 text-white hover:bg-cyan-700"
          >
            <Plus className="w-4 h-4" /> New bundle
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
          </div>
        ) : sorted.length === 0 ? (
          <div
            data-testid="ph31-bundle-list"
            className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500"
          >
            <div data-testid="ph31-empty-state">
              <Package className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              No bundle options yet. Create your first one above.
            </div>
          </div>
        ) : (
          <ul
            data-testid="ph31-bundle-list"
            className="grid grid-cols-1 md:grid-cols-2 gap-3"
          >
            {sorted.map((b) => (
              <li
                key={b.id}
                data-testid={`ph31-bundle-row-${b.id}`}
                className="rounded-2xl bg-white border border-slate-200 p-4 hover:border-cyan-300 cursor-pointer transition"
                onClick={() => openExisting(b)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">
                      {b.bundle_name}
                    </h3>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {BUNDLE_TYPE_LABEL[b.bundle_type] || b.bundle_type}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${statusPillClass(b.status)}`}
                  >
                    {STATUS_LABEL[b.status] || b.status}
                  </span>
                </div>
                <p className="mt-2 text-xs text-slate-600 line-clamp-2">
                  {b.description || "—"}
                </p>
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                  <span>
                    Price{" "}
                    {b.price_status === "hidden"
                      ? "(hidden)"
                      : `${PRICE_STATUS_LABEL[b.price_status] || ""} · ${fmtMoney(b.wholesale_price)}`}
                  </span>
                  <span>{b.visible_to_partner ? "Visible to partner" : "Admin-only"}</span>
                  <span>{(b.included_items || []).length} items</span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {editingId !== null && draft ? (
          <Editor
            mode={editingId === "" ? "create" : "edit"}
            meta={meta}
            draft={draft}
            setDraft={setDraft}
            saving={saving}
            onSave={save}
            onClose={closeEditor}
            onArchive={editingId
              ? () => archive(rows.find((r) => r.id === editingId))
              : null}
            audit={audit}
          />
        ) : null}
      </div>
    </PortalShell>
  );
}


function Editor({ mode, meta, draft, setDraft, saving, onSave, onClose, onArchive, audit }) {
  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateItem = (i, k, v) => {
    setDraft((d) => {
      const items = [...d.included_items];
      items[i] = { ...items[i], [k]: v };
      return { ...d, included_items: items };
    });
  };
  const addItem = () => update("included_items",
                                [...draft.included_items, { label: "", quantity: "", note: "" }]);
  const removeItem = (i) => update("included_items",
                                    draft.included_items.filter((_, idx) => idx !== i));

  return (
    <div
      data-testid="ph31-editor-panel"
      className="rounded-2xl bg-white border border-slate-200 p-6 space-y-5 relative"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900">
          {mode === "create" ? "New bundle" : "Edit bundle"}
        </h3>
        <button
          data-testid="ph31-close-editor-btn"
          onClick={onClose}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          Close
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Bundle name *
          </label>
          <input
            type="text"
            maxLength={160}
            data-testid="ph31-name-input"
            value={draft.bundle_name}
            onChange={(e) => update("bundle_name", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Bundle type
          </label>
          <select
            data-testid="ph31-type-select"
            value={draft.bundle_type}
            onChange={(e) => update("bundle_type", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          >
            {(meta?.bundle_types || Object.keys(BUNDLE_TYPE_LABEL)).map((t) => (
              <option key={t} value={t}>
                {BUNDLE_TYPE_LABEL[t] || t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Status
          </label>
          <select
            data-testid="ph31-status-select"
            value={draft.status}
            onChange={(e) => update("status", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          >
            {(meta?.bundle_statuses || Object.keys(STATUS_LABEL)).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Description
          </label>
          <textarea
            rows={2}
            maxLength={1000}
            data-testid="ph31-description-input"
            value={draft.description}
            onChange={(e) => update("description", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
            placeholder="Wholesale bundle option · visibility only · not an order, not a quote."
          />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Price status
          </label>
          <select
            data-testid="ph31-price-status-select"
            value={draft.price_status}
            onChange={(e) => update("price_status", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          >
            {(meta?.price_statuses || Object.keys(PRICE_STATUS_LABEL)).map((s) => (
              <option key={s} value={s}>
                {PRICE_STATUS_LABEL[s] || s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Wholesale price (AUD)
          </label>
          <input
            type="number" min={0} step="0.01"
            data-testid="ph31-wholesale-price-input"
            value={draft.wholesale_price}
            onChange={(e) => update("wholesale_price", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            RRP reference (AUD)
          </label>
          <input
            type="number" min={0} step="0.01"
            data-testid="ph31-rrp-input"
            value={draft.rrp_reference}
            onChange={(e) => update("rrp_reference", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Min order qty
          </label>
          <input
            type="number" min={0}
            data-testid="ph31-moq-input"
            value={draft.minimum_order_quantity}
            onChange={(e) => update("minimum_order_quantity", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Eligible partner type
          </label>
          <select
            data-testid="ph31-eligible-type-select"
            value={draft.eligible_partner_type}
            onChange={(e) => update("eligible_partner_type", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          >
            <option value="pws_franchisee">PWS Franchisee</option>
            <option value="any">Any</option>
          </select>
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Eligible partner status
          </label>
          <select
            data-testid="ph31-eligible-status-select"
            value={draft.eligible_partner_status}
            onChange={(e) => update("eligible_partner_status", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          >
            <option value="active">Active</option>
            <option value="any">Any</option>
          </select>
        </div>

        <div className="flex items-center gap-4 md:col-span-2 flex-wrap pt-1">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="ph31-visible-toggle"
              checked={draft.visible_to_partner}
              onChange={(e) => update("visible_to_partner", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-cyan-600"
            />
            <span className="text-sm text-slate-700">Visible to partner self-view</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="ph31-admin-only-toggle"
              checked={draft.admin_only}
              onChange={(e) => update("admin_only", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-cyan-600"
            />
            <span className="text-sm text-slate-700">Admin only (hides from partner view regardless)</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="ph31-gst-toggle"
              checked={draft.gst_inclusive}
              onChange={(e) => update("gst_inclusive", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-cyan-600"
            />
            <span className="text-sm text-slate-700">GST inclusive</span>
          </label>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Included items
            </label>
            <button
              data-testid="ph31-add-item-btn"
              onClick={addItem}
              className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add item
            </button>
          </div>
          {draft.included_items.length === 0 ? (
            <div className="text-xs text-slate-400 italic">No items yet.</div>
          ) : (
            <ul className="space-y-2">
              {draft.included_items.map((it, i) => (
                <li
                  key={i}
                  data-testid={`ph31-item-row-${i}`}
                  className="grid grid-cols-1 sm:grid-cols-[1fr,80px,1fr,32px] gap-2 items-center"
                >
                  <input
                    type="text"
                    placeholder="Label"
                    value={it.label}
                    onChange={(e) => updateItem(i, "label", e.target.value)}
                    data-testid={`ph31-item-label-${i}`}
                    className="rounded-md border-slate-300 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={it.quantity}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                    data-testid={`ph31-item-qty-${i}`}
                    className="rounded-md border-slate-300 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Note"
                    value={it.note}
                    onChange={(e) => updateItem(i, "note", e.target.value)}
                    data-testid={`ph31-item-note-${i}`}
                    className="rounded-md border-slate-300 text-sm"
                  />
                  <button
                    onClick={() => removeItem(i)}
                    data-testid={`ph31-item-remove-${i}`}
                    className="text-slate-400 hover:text-rose-600 text-xs"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Fulfilment note (visible to partner)
          </label>
          <input
            type="text"
            maxLength={600}
            data-testid="ph31-fulfilment-note-input"
            value={draft.fulfilment_note}
            onChange={(e) => update("fulfilment_note", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Internal note (admin only)
          </label>
          <textarea
            rows={2}
            maxLength={1000}
            data-testid="ph31-internal-note-input"
            value={draft.internal_note}
            onChange={(e) => update("internal_note", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-slate-200">
        <button
          data-testid="ph31-save-btn"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : (mode === "create" ? "Create bundle" : "Save changes")}
        </button>
        {onArchive ? (
          <button
            data-testid="ph31-archive-btn"
            onClick={onArchive}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
          >
            <Archive className="w-4 h-4" /> Archive
          </button>
        ) : null}
        <div className="text-[11px] text-amber-700 inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          No order is placed, no fulfilment triggered, no payment taken.
        </div>
      </div>

      {audit && audit.length > 0 ? (
        <div
          data-testid="ph31-audit-panel"
          className="pt-4 border-t border-slate-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <History className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
              Audit history
            </h4>
            <span className="text-xs text-slate-400">
              (append-only — last {audit.length})
            </span>
          </div>
          <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
            {audit.map((row, i) => (
              <li
                key={row.id || i}
                data-testid={`ph31-audit-row-${i}`}
                className="py-2 text-xs text-slate-700"
              >
                <span className="font-mono text-slate-500">
                  {fmtAU(row.created_at)}
                </span>{" "}
                · <span className="font-semibold">{row.action}</span>{" "}
                · {row.actor_email || "system"}
                {row.changes && row.changes.length > 0 ? (
                  <div className="text-slate-500 mt-0.5">
                    Changed: {row.changes.join(", ")}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
